import React, { useEffect, useRef, useState } from "react";
import { ArrowLeftRegular } from "@fluentui/react-icons";
import {
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import { useFormContext } from "react-hook-form";
import { OrderFormData, Product } from "../../interfaces/OrderFormData";
import { OrderInformation } from "./steps/OrderInformation";
import { Basket } from "./Basket";
import { OrderAddons } from "./steps/OrderAddons";
import {
  formatOrderEmail,
  getSubscriptionFromEnum,
  sendOrderEmail,
} from "../../helper/orderHelper";
import { MessageInput, Plan } from "../../graphql/schema";
import { SubscriptionLevel } from "../../interfaces/SubscriptionLevel";
import { CtaButton } from "../common/input/CtaButton";
import { OrderHeader } from "./OrderHeader";
import { useNavigate } from "react-router-dom";
import { OrderConfirm } from "./steps/OrderConfirm";
import { t } from "../../i18n/i18n";
import { clarityEvent } from "../../lib/clarity";

const useStyles = makeStyles({
  sectionWrapper: {
    width: "95%",
    ...shorthands.margin(0, "auto"),
    paddingTop: "10vh",
    paddingBottom: tokens.spacingHorizontalL,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    "@media screen and (min-width: 700px)": {
      width: "90%",
      maxWidth: "80vw",
      ...shorthands.padding(
        tokens.spacingHorizontalXXXL,
        tokens.spacingVerticalXXXL,
      ),
    },
  },
  headerRow: {
    marginBottom: tokens.spacingVerticalXXL,
  },
  contentRow: {
    display: "flex",
    flexDirection: "column",
    rowGap: tokens.spacingVerticalXXL,

    "@media screen and (min-width: 700px)": {
      flexDirection: "row",
      columnGap: tokens.spacingHorizontalM,
      alignItems: "flex-start",
    },
  },
  formColumn: {
    order: 1,

    "@media screen and (min-width: 700px)": {
      order: 1,
      flexGrow: 5,
      flexShrink: 1,
      flexBasis: "0%",
      minWidth: 0,
    },
    "@media screen and (min-width: 1200px)": {
      flexGrow: 65,
    },
  },
  basketColumn: {
    order: 2,

    "@media screen and (min-width: 700px)": {
      order: 2,
      flexGrow: 7,
      flexShrink: 1,
      flexBasis: "0%",
      position: "sticky",
      top: "5vh",
    },
    "@media screen and (min-width: 1200px)": {
      flexGrow: 35,
    },
  },
  orderWrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "start",
    overflowX: "hidden",
    minWidth: 0,
    ...shorthands.padding(
      tokens.spacingVerticalXXXL,
      tokens.spacingHorizontalXXXL,
    ),
    ...shorthands.borderRadius("20px"),
    rowGap: tokens.spacingVerticalM,
    color: tokens.colorBrandBackground,
    backgroundColor: "#FFFFFF",
    "@media screen and (max-width: 900px)": {
      ...shorthands.padding(
        tokens.spacingVerticalXL,
        tokens.spacingHorizontalL,
      ),
    },
    "@media screen and (max-width: 750px)": {
      ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    },
  },
  basketWrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "start",
    ...shorthands.padding(
      tokens.spacingVerticalXXXL,
      tokens.spacingHorizontalXXXL,
    ),
    ...shorthands.borderRadius("20px"),
    rowGap: tokens.spacingVerticalM,
    color: tokens.colorBrandBackground,
    backgroundColor: "#FFFFFF",
    "@media screen and (max-width: 900px)": {
      ...shorthands.padding(
        tokens.spacingVerticalXL,
        tokens.spacingHorizontalL,
      ),
    },
  },
  backButton: {
    justifyContent: "start",
    ...shorthands.padding(0, tokens.spacingVerticalS),
    ...shorthands.margin(0),
  },
  backButtonDesktop: {
    "@media screen and (max-width: 699px)": {
      display: "none",
    },
  },
  backButtonMobile: {
    display: "none",
    ...shorthands.margin(0, 0, tokens.spacingVerticalM, 0),
    "@media screen and (max-width: 699px)": {
      display: "block",
    },
  },
});

type OrderDetailsProps = {
  subscriptionData: Plan[];
  setOrderCompleted: React.Dispatch<React.SetStateAction<boolean>>;
  showPrices?: boolean;
  defaultUserPrice?: number;
  defaultWorkplacePrice?: number;
  defaultProcessPrice?: number;
};

export const OrderDetails = ({
  subscriptionData,
  setOrderCompleted,
  showPrices,
  defaultUserPrice = 0,
  defaultWorkplacePrice = 0,
  defaultProcessPrice = 0,
}: OrderDetailsProps) => {
  const classes = useStyles();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [step, setStep] = useState(1);
  const { watch, trigger, setValue } = useFormContext<OrderFormData>();
  const navigate = useNavigate();

  const handleNext = async () => {
    const valid = await trigger();
    if (!valid) return;

    clarityEvent(`order_step${step}_completed`);
    setStep(step === 1 ? 3 : step + 1);
  };
  const handleBack = () => {
    if (step === 1) navigate("/");
    setStep(step === 3 ? 1 : step - 1);
  };

  const onSubmit = async (data: OrderFormData) => {
    const valid = await trigger();
    if (!valid) return;

    // map data to email format
    const message: MessageInput = {
      email: data.contactDetails.contactEmail,
      message: formatOrderEmail(data),
      name: `${data.contactDetails.firstName} ${data.contactDetails.lastName}`,
      policyApproved: true,
    };

    setLoading(true);
    setError(null);

    try {
      await sendOrderEmail(message);

      clarityEvent("order_submitted");
      setOrderCompleted(true);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      clarityEvent("order_submit_error");
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const subEnum = watch("subscription");
  const numberOfUsers = watch("numberOfUsers");
  const numberOfWorkplaces = watch("numberOfWorkplaces");
  const processes = watch("processes");
  const prevSubRef = useRef<SubscriptionLevel | null>(null);

  useEffect(() => {
    if (subEnum === SubscriptionLevel.CUSTOM) {
      const products: Product[] = [
        {
          name: "Användare",
          quantity: numberOfUsers,
          monthlyCost: numberOfUsers * defaultUserPrice,
        },
        {
          name: "Arbetsställe",
          quantity: numberOfWorkplaces,
          monthlyCost: numberOfWorkplaces * defaultWorkplacePrice,
        },
      ];
      if (processes.length > 0) {
        products.push({
          name: "Processer",
          quantity: processes.length,
          monthlyCost: processes.length * defaultProcessPrice,
        });
      }
      setValue("products", products);
      return;
    }

    const selectedSub = getSubscriptionFromEnum(subscriptionData, subEnum);
    if (!selectedSub) return;

    const subscription = selectedSub;

    const includedUsers = subscription.users ?? 1;
    const includedWorkplaces = subscription.workplaces ?? 1;
    const includedProcesses = subscription.includedProcesses ?? 0;

    const extraUsers = Math.max(numberOfUsers - includedUsers, 0);
    const extraWorkplaces = Math.max(
      numberOfWorkplaces - includedWorkplaces,
      0,
    );
    const extraProcesses = Math.max(processes.length - includedProcesses, 0);

    const products: Product[] = [
      {
        name: `Paket ${subscription.title}`,
        quantity: 1,
        monthlyCost: Number(subscription.price) || 0,
      },
      {
        name: `Användare`,
        quantity: includedUsers,
        monthlyCost: 0,
      },
      {
        name: `Arbetställe`,
        quantity: includedWorkplaces,
        monthlyCost: 0,
      },
    ];

    if (includedProcesses > 0) {
      products.push({
        name: "Processer",
        quantity: includedProcesses,
        monthlyCost: 0,
      });
    }

    if (extraUsers > 0) {
      products.push({
        name: "Extra användare",
        quantity: extraUsers,
        monthlyCost: extraUsers * defaultUserPrice,
      });
    }

    if (extraWorkplaces > 0) {
      products.push({
        name: "Extra arbetsställe",
        quantity: extraWorkplaces,
        monthlyCost: extraWorkplaces * defaultWorkplacePrice,
      });
    }

    if (extraProcesses > 0) {
      products.push({
        name: "Extra processer",
        quantity: extraProcesses,
        monthlyCost: extraProcesses * defaultProcessPrice,
      });
    }

    setValue("products", products);
  }, [
    subEnum,
    numberOfUsers,
    numberOfWorkplaces,
    processes,
    subscriptionData,
    setValue,
    defaultUserPrice,
    defaultWorkplacePrice,
    defaultProcessPrice,
  ]);

  useEffect(() => {
    const selectedSub = getSubscriptionFromEnum(subscriptionData, subEnum);
    if (!selectedSub) return;

    const subscription = selectedSub;

    // Reset if subscription changes
    if (prevSubRef.current !== subEnum) {
      const includedUsers = subscription.users ?? 1;
      const includedWorkplaces = subscription.workplaces ?? 1;
      setValue("numberOfUsers", includedUsers);
      setValue("numberOfWorkplaces", includedWorkplaces);

      prevSubRef.current = subEnum;
    }
  }, [subEnum, subscriptionData, setValue]);

  // Sync included addons and base modules to form whenever the subscription changes,
  // so the basket reflects the correct inclusions at any step.
  useEffect(() => {
    if (subEnum === SubscriptionLevel.CUSTOM) {
      setValue("additionalModules", [], { shouldDirty: false });
      setValue("baseModules", [], { shouldDirty: false });
      return;
    }
    const selectedSub = getSubscriptionFromEnum(subscriptionData, subEnum);
    if (!selectedSub) return;

    const includedAddons =
      selectedSub.additionalModules
        ?.filter((a): a is NonNullable<typeof a> => !!a)
        .map((a) => ({
          id: a.documentId,
          name: a.name ?? "",
          monthlyCost: 0,
        })) ?? [];
    setValue("additionalModules", includedAddons, { shouldDirty: false });

    const includedBaseModules =
      selectedSub.baseModules
        ?.filter((bm): bm is NonNullable<typeof bm> => !!bm)
        .map((bm) => ({
          id: bm.documentId,
          name: bm.name ?? "",
          monthlyCost: 0,
        })) ?? [];
    setValue("baseModules", includedBaseModules, { shouldDirty: false });
  }, [subEnum, subscriptionData, setValue]);

  const renderStep = () => {
    switch (step) {
      case 1:
        return <OrderInformation />;
      case 2:
        return (
          <OrderAddons
            subscriptionData={subscriptionData}
            showPrices={showPrices}
          />
        );
      case 3:
        return <OrderConfirm />;
      default:
        return null;
    }
  };

  return (
    <div className={classes.sectionWrapper}>
      <div className={classes.headerRow}>
        <OrderHeader step={step} />
        <CtaButton
          color="subtle"
          buttonText={t("Back")}
          className={mergeClasses(
            classes.backButton,
            classes.backButtonDesktop,
          )}
          icon={<ArrowLeftRegular />}
          onClick={handleBack}
        />
      </div>
      <div className={classes.contentRow}>
        <div className={mergeClasses(classes.formColumn, classes.orderWrapper)}>
          <div className={classes.backButtonMobile}>
            <CtaButton
              color="subtle"
              buttonText={t("Back")}
              className={classes.backButton}
              icon={<ArrowLeftRegular />}
              onClick={handleBack}
            />
          </div>
          {renderStep()}
        </div>
        <div className={classes.basketColumn}>
          <div className={classes.basketWrapper}>
            <Basket
              onNextClick={handleNext}
              onSubmitClick={onSubmit}
              step={step}
              loading={loading}
              showPrices={showPrices}
            />
            {error && (
              <div style={{ color: "red", marginTop: 8 }}>{error.message}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
